import { useMutation } from '@apollo/client';
import { UPLOAD_FILE } from '@core/generic/uploadFile.gql';
import { AssetFileType, UploadType } from '@core/generated/gql-types';
import { getFriendlyUploadNetworkErrorMessage } from '@core/util/uploadNetworkError.util';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface FileUploadProps {
  onUploadComplete: (url: string) => void;
  uploadType: UploadType;
  className?: string;
  children?: React.ReactNode;
  accept?: string;
  disabled?: boolean;
  showPreview?: boolean;
  previewClassName?: string;
  objectId?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadComplete,
  uploadType,
  className = '',
  children,
  accept = 'image/*',
  disabled = false,
  showPreview = false,
  previewClassName = '',
  objectId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [uploadFile] = useMutation(UPLOAD_FILE);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Create preview if enabled
      if (showPreview) {
        const preview = URL.createObjectURL(file);
        setPreviewUrl(preview);
      }

      const reader = new FileReader();
      reader.onerror = () => {
        setIsUploading(false);
        toast.error('Failed to read file. Please try again.', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      };
      reader.onload = async () => {
        try {
          const base64String = reader.result as string;
          const fileType = file.type
            .split('/')[1]
            .toUpperCase() as AssetFileType;

          const { data } = await uploadFile({
            variables: {
              input: {
                fileBytes: base64String.split(',')[1],
                fileType,
                type: uploadType,
                objectId,
              },
            },
          });

          if (data?.uploadFile?.url) {
            onUploadComplete(data.uploadFile.url);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          toast.error(
            getFriendlyUploadNetworkErrorMessage(
              error,
              'Failed to upload file. Please try again.',
            ),
            { position: 'bottom-left', autoClose: 3000 },
          );
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      setIsUploading(false);
      toast.error(
        getFriendlyUploadNetworkErrorMessage(
          error,
          'Failed to upload file. Please try again.',
        ),
        { position: 'bottom-left', autoClose: 3000 },
      );
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={handleClick}
        className={`cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        {showPreview && previewUrl ? (
          <div className={previewClassName}>
            <img
              src={previewUrl}
              alt="Preview"
              className="h-full w-full object-cover"
            />
            <div className="bg-opacity-0 hover:bg-opacity-40 absolute inset-0 flex items-center justify-center bg-black/50 transition-all">
              <span className="text-xs font-medium text-white opacity-0 hover:opacity-100">
                {isUploading ? 'Uploading...' : 'Change'}
              </span>
            </div>
          </div>
        ) : (
          children
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled || isUploading}
      />
    </div>
  );
};

export default FileUpload;
